import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { formatApiErrorDetail } from "@/lib/api";
import { ArrowRight } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", institution: "", student_number: "", phone: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Profile created — welcome to Haki");
      navigate("/app/profile");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const field = (label, key, type = "text", placeholder = "", required = false) => (
    <div>
      <label className="block text-sm font-medium mb-1.5">
        {label} {!required && <span className="text-muted-foreground text-xs">(optional)</span>}
      </label>
      <input
        type={type}
        required={required}
        value={form[key]}
        onChange={set(key)}
        data-testid={`register-${key}-input`}
        placeholder={placeholder}
        className="w-full bg-secondary border border-border rounded-xl px-3.5 py-3 text-sm outline-none focus:ring-2 focus:ring-primary focus:border-primary"
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-background relative z-10 flex flex-col">
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center">
          <Link to="/"><Logo /></Link>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-md">
          <p className="text-xs font-medium tracking-overline text-primary uppercase mb-3">Get credit today</p>
          <h1 className="text-3xl font-bold tracking-tight mb-8">Create your student profile</h1>
          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            {field("Full name", "name", "text", "Thandi Mabuza", true)}
            {field("Email", "email", "email", "you@campus.ac.za", true)}
            {field("Password", "password", "password", "Choose a strong password", true)}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field("Institution", "institution", "text", "University / TVET")}
              {field("Student number", "student_number", "text", "e.g. 2100123")}
            </div>
            {field("Phone", "phone", "tel", "+27 …")}
            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="w-full bg-primary text-white rounded-full font-medium py-3 inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform duration-150 disabled:opacity-60"
            >
              {loading ? "Creating…" : <>Create profile <ArrowRight size={16} /></>}
            </button>
          </form>
          <p className="mt-6 text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" data-testid="register-to-login" className="text-primary font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
